import type { ConcentrationTimePoint } from '@bioavail/shared-types';

/**
 * Log-linear regression on ln(concentration) vs time (PSD §4.2.5).
 * Returns slope, intercept and R². Pure function.
 */
export function logLinearRegression(
  points: { time: number; concentration: number }[],
): { slope: number; intercept: number; r2: number } {
  const eligible = points.filter((p) => p.concentration > 0);
  if (eligible.length < 3) {
    throw new Error('Log-linear regression requires at least 3 positive-concentration points.');
  }

  const n = eligible.length;
  const t = eligible.map((p) => p.time);
  const y = eligible.map((p) => Math.log(p.concentration));

  const tMean = t.reduce((a, b) => a + b, 0) / n;
  const yMean = y.reduce((a, b) => a + b, 0) / n;

  let numerator = 0;
  let denominator = 0;
  for (let i = 0; i < n; i++) {
    numerator += (t[i]! - tMean) * (y[i]! - yMean);
    denominator += (t[i]! - tMean) ** 2;
  }

  if (denominator === 0) {
    throw new Error('Log-linear regression failed: all times are identical (degenerate input).');
  }

  const slope = numerator / denominator;
  const intercept = yMean - slope * tMean;

  let ssRes = 0;
  let ssTot = 0;
  for (let i = 0; i < n; i++) {
    const yPred = slope * t[i]! + intercept;
    ssRes += (y[i]! - yPred) ** 2;
    ssTot += (y[i]! - yMean) ** 2;
  }

  const r2 = ssTot === 0 ? 1 : 1 - ssRes / ssTot;

  return { slope, intercept, r2 };
}

/**
 * Convenience wrapper operating on ConcentrationTimePoint[] sorted by time.
 */
export function regress(points: ConcentrationTimePoint[]): {
  slope: number;
  intercept: number;
  r2: number;
} {
  return logLinearRegression(
    points.map((p) => ({ time: p.time, concentration: p.concentration })),
  );
}
